import { ReactNode } from "react";

import { Link } from "react-router";

import { Box, Flex } from "@/components/layout/flexbox";
import { buildScenarioUrl } from "@/components/pages_logged_in/roleplays/utils";
import { DifficultyTag } from "@/components/pieces/roleplays/DifficultyTag";
import { AvatarWithPrettyNull } from "@/components/pieces/users/AvatarWithPrettyNull";
import { ScenarioListItemFragment } from "@/graphql";

interface ScenarioSimpleCardProps {
  scenario: ScenarioListItemFragment;
  roleplaySlug?: string | null;
  showDifficulty?: boolean;
  showShortDesc?: boolean;
  isInteractive?: boolean;
  rightContent?: ReactNode;
}

export default function ScenarioSimpleCard({
  scenario,
  roleplaySlug = null,
  showDifficulty = true,
  showShortDesc = true,
  isInteractive = true,
  rightContent,
}: ScenarioSimpleCardProps) {
  const link = buildScenarioUrl({
    roleplaySlug: roleplaySlug ?? undefined,
    scenarioSlug: scenario.slug,
  });

  const baseClasses =
    "bas round-corners flex items-center justify-between bg-white text-inherit p-4 w-full flex-col sm:flex-row";
  const interactiveClasses = isInteractive
    ? "hover:border-blue-700 hover:cursor-pointer hover:shadow-[0_1px_1px_rgba(24,32,38,0.1),0_2px_6px_rgba(24,32,38,0.05)] focus:border-blue-700 focus:shadow-[0_1px_1px_rgba(24,32,38,0.1),0_2px_6px_rgba(24,32,38,0.05)] active:border-blue-700 active:shadow-[0_1px_1px_rgba(24,32,38,0.1),0_2px_6px_rgba(24,32,38,0.05)]"
    : "";

  const cardContent = (
    <Flex
      alignItems="center"
      flexDirection={["column", "row"]}
      width="100%"
      className="gap-3"
    >
      <Flex alignItems="center" flexDirection={["column", "row"]} flex={1}>
        <Box minWidth={80}>
          <AvatarWithPrettyNull
            user={{
              name: scenario.personaName,
              photo: scenario.personaPhotoUrl,
            }}
            size={80}
          />
        </Box>
        <Box mx={[0, 3]} textAlign={["center", "left"]} mt={[3, 0]} flex={1}>
          <Box fontWeight={500} fontSize={16}>
            {scenario.name}
          </Box>
          {scenario.shortDesc && showShortDesc && (
            <Box
              mt={1}
              className="text-gray-600 overflow-hidden text-ellipsis line-clamp-2"
            >
              {scenario.shortDesc}
            </Box>
          )}
          {showDifficulty && scenario.difficulty && (
            <Box mt={2}>
              <DifficultyTag difficulty={scenario.difficulty} />
            </Box>
          )}
        </Box>
      </Flex>
      {rightContent && (
        <div className="flex items-center ml-auto">{rightContent}</div>
      )}
    </Flex>
  );

  if (isInteractive) {
    return (
      <Link to={link} className={`${baseClasses} ${interactiveClasses}`}>
        {cardContent}
      </Link>
    );
  }

  return <div className={baseClasses}>{cardContent}</div>;
}
